import {
  LanguageNameSpaces,
  OneClickFollowBotData,
  SymbolClientsData,
  UsageType,
} from "@shared/types";
import { FollowSignalType } from "@shared/features/signals/common/types.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import { useContext } from "react";
import { FollowSignalsContext } from "@shared/features/signals/common/FollowSignals.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import useQuotaModal from "features/settings/Quota/useQuotaModal.tsx";
import { displayResultMessage, openMessage } from "utils/misc.tsx";
import { useExchangesMetadata } from "@shared/utils/hooks/misc.ts";

const useFollowSignalModal = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const {
    getExchangeSignalIdsStatus,
    getBotsOneClickDataStatus,
    groupData,
    isFetching,
    getBotAccountData,
    signal,
    followType,
  } = useContext(FollowSignalsContext)!;
  const [
    oneClickFollowSignal,
    { isLoading: isLoadingSignalFollow, data: signalFollowResponse, reset },
  ] = sharedLib.sharedApi.signals.useOneClickFollowSignalMutation();
  const exchangeSignalIds = getExchangeSignalIdsStatus.data;
  const botsData = getBotsOneClickDataStatus.data;
  const isEmptyBots = !botsData?.length;
  const { getIsFree } = useExchangesMetadata();

  const {
    quotaModal,
    handleTriggerClick,
    isLoading: isLoadingQuota,
  } = useQuotaModal({
    usageType: UsageType.ACTIVE_TRADE,
  });

  const successText =
    followType !== undefined
      ? t("modals.follow_signal.success_message", {
          action: t(SUCCESS_TEXT_MAPPING[followType]),
        })
      : undefined;

  const onConfirm = async (
    _accountData?: SymbolClientsData,
    _botData?: OneClickFollowBotData
  ) => {
    const accountData = _accountData ?? getBotAccountData(_botData);
    if (!_botData) {
      openMessage(t("modals.follow_signal.warning"), "warning");
      return;
    } else if (!exchangeSignalIds || !accountData) return;
    let isQuotaError = false;
    if (!getIsFree(accountData.exchange)) {
      try {
        isQuotaError = !!(await handleTriggerClick({
          ignoreQuotaCheck: followType !== FollowSignalType.FOLLOW,
        }));
      } catch {}
    }
    if (!isQuotaError) {
      return displayResultMessage(
        oneClickFollowSignal({
          client_id: accountData.id,
          exchange_signal_id: exchangeSignalIds?.[accountData.exchange],
          bot_id: _botData?.id,
        }).unwrap(),
        successText,
        undefined,
        5
      );
    }
  };

  const onCloseModal = () => {
    setTimeout(() => reset(), 400);
  };

  const analyticsArgs =
    followType && signal
      ? {
          followSignalModalType: ANALYTICS_MODAL_TYPE_MAPPING[followType],
          groupSignalId: signal?.signal_id,
          groupChannelId: signal?.sub_group_id,
        }
      : undefined;

  const getDescription = () => {
    if (isEmptyBots) return;

    return followType !== undefined
      ? t(MODAL_DESCRIPTION_MAPPING[followType])
      : undefined;
  };

  return {
    isFetching,
    isLoadingSignalFollow,
    onConfirm,
    title:
      followType !== undefined ? t(MODAL_TITLE_MAPPING[followType]) : undefined,
    getDescription,
    successText,
    signalFollowResponse,
    groupData,
    onCloseModal,
    analyticsArgs,
    isEmptyBots,
    quotaModal,
    isLoadingQuota,
  };
};

export default useFollowSignalModal;

const MODAL_DESCRIPTION_MAPPING: Record<
  FollowSignalType,
  TranslationKeys<LanguageNameSpaces.SIGNALS>
> = {
  [FollowSignalType.FOLLOW]: "modals.follow_signal.descriptions.follow",
  [FollowSignalType.UPDATE]: "modals.follow_signal.descriptions.update",
  [FollowSignalType.CANCEL]: "modals.follow_signal.descriptions.cancel",
};
const SUCCESS_TEXT_MAPPING: Record<
  FollowSignalType,
  TranslationKeys<LanguageNameSpaces.SIGNALS>
> = {
  [FollowSignalType.FOLLOW]: "modals.follow_signal.success_action.created",
  [FollowSignalType.UPDATE]: "modals.follow_signal.success_action.updated",
  [FollowSignalType.CANCEL]: "modals.follow_signal.success_action.closed",
};
const ANALYTICS_MODAL_TYPE_MAPPING = {
  [FollowSignalType.FOLLOW]: "Create Trade",
  [FollowSignalType.UPDATE]: "Update Trade",
  [FollowSignalType.CANCEL]: "Close Trade",
} as const;
const MODAL_TITLE_MAPPING: Record<
  FollowSignalType,
  TranslationKeys<LanguageNameSpaces.SIGNALS>
> = {
  [FollowSignalType.FOLLOW]: "actions.follow_signal",
  [FollowSignalType.UPDATE]: "actions.update_trade",
  [FollowSignalType.CANCEL]: "actions.update_trade",
};
